import { NotificationType } from './notification-types';
import type {
  IWechatOfficialTemplateBindings,
  IWechatTemplateVariableDefinition,
} from '../types/wechat-official';

/** 支持微信服务号模板投递的七类业务通知；系统公告仅保留站内通知。 */
export const WECHAT_NOTIFICATION_TYPES = [
  NotificationType.CRM_RECORD_NEW,
  NotificationType.CRM_RECORD_ASSIGNED,
  NotificationType.CRM_RECORD_RETURNED_POOL,
  NotificationType.CRM_RECORD_TRANSFERRED_POOL,
  NotificationType.CRM_RECORD_DEAL,
  NotificationType.CRM_FOLLOW_UP_REMINDER,
  NotificationType.CRM_FOLLOW_UP_REPORT,
] as const;

export type WechatNotificationType = (typeof WECHAT_NOTIFICATION_TYPES)[number];

export interface WechatOfficialTemplateFieldDefinition {
  label: string;
  key: string;
}

export interface WechatOfficialTemplateDefinition {
  title: string;
  templateNumber: string;
  defaultTemplateId: string;
  fields: readonly WechatOfficialTemplateFieldDefinition[];
}

/**
 * 服务号后台已选用的模板定义。
 *
 * 字段 Key 与模板结构绑定，由代码负责构建和校验；设置页只允许更换模板 ID。
 */
export const WECHAT_OFFICIAL_TEMPLATE_DEFINITIONS: Record<
  WechatNotificationType,
  WechatOfficialTemplateDefinition
> = {
  [NotificationType.CRM_RECORD_NEW]: {
    title: '新增客户提醒',
    templateNumber: '29036',
    defaultTemplateId: 'Haq5UMfuNpFno0CgB8HbXtmx8-6O9YtfLF1o4t5AbdQ',
    fields: [
      { label: '联系人', key: 'thing5' },
      { label: '联系方式', key: 'phone_number6' },
      { label: '销售人员', key: 'name1' },
      { label: '所在省市', key: 'thing10' },
    ],
  },
  [NotificationType.CRM_RECORD_ASSIGNED]: {
    title: '客户分配提醒',
    templateNumber: '28783',
    defaultTemplateId: 'DZRBnz4ykMmBICDs25rAwqyHk0rhDgOQTcZxc5I49co',
    fields: [
      { label: '客户姓名', key: 'name1' },
      { label: '客户手机', key: 'phone_number2' },
      { label: '客户ID', key: 'character_string3' },
      { label: '分配详情', key: 'thing5' },
    ],
  },
  [NotificationType.CRM_RECORD_RETURNED_POOL]: {
    title: '客户即将退回公海通知',
    templateNumber: '47518',
    defaultTemplateId: '83AB80lNlkmRLgDsjbQQC4pB9vJ-B-Jtymvo5iMks1M',
    fields: [
      { label: '客户名称', key: 'thing5' },
      { label: '所属公海', key: 'thing2' },
      { label: '负责人', key: 'thing3' },
      { label: '退回时间', key: 'time6' },
    ],
  },
  [NotificationType.CRM_RECORD_TRANSFERRED_POOL]: {
    title: '客户转移通知',
    templateNumber: '29623',
    defaultTemplateId: 'AGW0aGGgXIqCZlUr8f_PGkLTVqeO6M9ZV1IDG4JVKDU',
    fields: [
      { label: '客户编号', key: 'character_string1' },
      { label: '手机号码', key: 'phone_number2' },
      { label: '转移内容', key: 'thing6' },
    ],
  },
  [NotificationType.CRM_RECORD_DEAL]: {
    title: '订单成交通知',
    templateNumber: '18700',
    defaultTemplateId: '1WWrhZ4nMdZ-fpY6dVfj2zASxAdKBpbrOwAW8XUOPTA',
    fields: [
      { label: '订单编号', key: 'character_string2' },
      { label: '订单金额', key: 'amount3' },
      { label: '温馨提示', key: 'thing5' },
    ],
  },
  [NotificationType.CRM_FOLLOW_UP_REMINDER]: {
    title: '客户跟进提醒',
    templateNumber: '29047',
    defaultTemplateId: 'zXzH_EhvR8CDd1nA9G29FtY078zJxiWeR4phvTHZObI',
    fields: [
      { label: '客户姓名', key: 'thing1' },
      { label: '客户电话', key: 'phone_number2' },
      { label: '上次时间', key: 'time3' },
      { label: '客户星级', key: 'short_thing16' },
    ],
  },
  [NotificationType.CRM_FOLLOW_UP_REPORT]: {
    title: '工作汇报通知',
    templateNumber: '28944',
    defaultTemplateId: 'LYwGGPQLRfoqPhp9jTYFmQX2rfGKjOLh4bPTRPxw5dU',
    fields: [
      { label: '汇报人', key: 'name2' },
      { label: '汇报标题', key: 'thing6' },
      { label: '备注', key: 'thing4' },
    ],
  },
};

export const DEFAULT_WECHAT_OFFICIAL_TEMPLATE_IDS = Object.fromEntries(
  WECHAT_NOTIFICATION_TYPES.map((type) => [
    type,
    WECHAT_OFFICIAL_TEMPLATE_DEFINITIONS[type].defaultTemplateId,
  ]),
) as Record<WechatNotificationType, string>;

const DEFAULT_FIELD_EXPRESSIONS: Record<
  WechatNotificationType,
  Record<string, string>
> = {
  [NotificationType.CRM_RECORD_NEW]: {
    thing5: '{{customer.name}}',
    phone_number6: '{{customer.phone}}',
    name1: '{{salesperson.name}}',
    thing10: '{{customer.region}}',
  },
  [NotificationType.CRM_RECORD_ASSIGNED]: {
    name1: '{{customer.name}}',
    phone_number2: '{{customer.phone}}',
    character_string3: '{{customer.recordCode}}',
    thing5: '{{assignment.source}}分配至{{salesperson.name}}',
  },
  [NotificationType.CRM_RECORD_RETURNED_POOL]: {
    thing5: '{{customer.name}}',
    thing2: '{{company.name}}公海',
    thing3: '{{salesperson.name}}',
    time6: '{{event.time}}',
  },
  [NotificationType.CRM_RECORD_TRANSFERRED_POOL]: {
    character_string1: '{{customer.recordCode}}',
    phone_number2: '{{customer.phone}}',
    thing6: '{{sourceCompany.name}}转至{{targetCompany.name}}：{{event.reason}}',
  },
  [NotificationType.CRM_RECORD_DEAL]: {
    character_string2: '{{customer.recordCode}}',
    amount3: '{{deal.amount}}',
    thing5: '客户已成交，请及时安排后续服务',
  },
  [NotificationType.CRM_FOLLOW_UP_REMINDER]: {
    thing1: '{{customer.name}}',
    phone_number2: '{{customer.phone}}',
    time3: '{{followUp.lastAt}}',
    short_thing16: '{{customer.grade}}级',
  },
  [NotificationType.CRM_FOLLOW_UP_REPORT]: {
    name2: '{{followUp.reporter}}',
    thing6: '客户跟进：{{customer.name}}',
    thing4: '{{followUp.content}}',
  },
};

export const DEFAULT_WECHAT_OFFICIAL_TEMPLATE_BINDINGS = Object.fromEntries(
  WECHAT_NOTIFICATION_TYPES.map((type) => {
    const definition = WECHAT_OFFICIAL_TEMPLATE_DEFINITIONS[type];
    return [
      type,
      {
        enabled: true,
        templateId: definition.defaultTemplateId,
        displayName: definition.title,
        templateTitle: definition.title,
        templateContent: definition.fields
          .map((field) => `${field.label}：{{${field.key}.DATA}}`)
          .join('\n'),
        fields: definition.fields.map((field) => ({
          label: field.label,
          fieldKey: field.key,
          expression: DEFAULT_FIELD_EXPRESSIONS[type][field.key] ?? '',
        })),
      },
    ];
  }),
) as IWechatOfficialTemplateBindings;

function variable(
  key: string,
  label: string,
  group: string,
  example: string,
): IWechatTemplateVariableDefinition {
  return { key, label, group, example };
}

const COMMON_VARIABLES = [
  variable('customer.name', '客户名称', '客户', '测试客户'),
  variable('customer.phone', '客户手机号', '客户', '13800138000'),
  variable('customer.recordCode', '客户编号', '客户', 'TEST0001'),
  variable('customer.region', '客户省市', '客户', '北京市'),
  variable('customer.grade', '客户等级', '客户', 'A'),
] as const;

/** 各通知场景允许使用的稳定变量；自定义字段由后端动态追加。 */
export const WECHAT_TEMPLATE_VARIABLES: Record<
  WechatNotificationType,
  readonly IWechatTemplateVariableDefinition[]
> = {
  [NotificationType.CRM_RECORD_NEW]: [
    ...COMMON_VARIABLES,
    variable('salesperson.name', '销售人员', '人员', '测试销售'),
  ],
  [NotificationType.CRM_RECORD_ASSIGNED]: [
    ...COMMON_VARIABLES,
    variable('salesperson.name', '销售人员', '人员', '测试销售'),
    variable('assignment.source', '分配方式', '事件', '人工'),
  ],
  [NotificationType.CRM_RECORD_RETURNED_POOL]: [
    ...COMMON_VARIABLES,
    variable('company.name', '所属公司', '公司', '测试公司'),
    variable('salesperson.name', '原负责人', '人员', '测试销售'),
    variable('event.reason', '退回原因', '事件', '客户暂不考虑'),
    variable('event.time', '退回时间', '事件', '2026年08月05日 09:00'),
  ],
  [NotificationType.CRM_RECORD_TRANSFERRED_POOL]: [
    ...COMMON_VARIABLES,
    variable('sourceCompany.name', '来源公司', '公司', '来源公司'),
    variable('targetCompany.name', '目标公司', '公司', '目标公司'),
    variable('event.reason', '转移原因', '事件', '跨区域协作'),
    variable('event.time', '转移时间', '事件', '2026年08月05日 09:00'),
  ],
  [NotificationType.CRM_RECORD_DEAL]: [
    ...COMMON_VARIABLES,
    variable('company.name', '所属公司', '公司', '测试公司'),
    variable('salesperson.name', '负责人', '人员', '测试销售'),
    variable('deal.amount', '成交金额', '成交', '¥12888.00'),
    variable('event.time', '成交时间', '事件', '2026年08月05日 09:00'),
  ],
  [NotificationType.CRM_FOLLOW_UP_REMINDER]: [
    ...COMMON_VARIABLES,
    variable('salesperson.name', '负责人', '人员', '测试销售'),
    variable('followUp.lastAt', '上次跟进时间', '跟进', '2026年08月04日 15:30'),
    variable('reminder.date', '提醒日期', '跟进', '2026年08月05日'),
  ],
  [NotificationType.CRM_FOLLOW_UP_REPORT]: [
    ...COMMON_VARIABLES,
    variable('company.name', '所属公司', '公司', '测试公司'),
    variable('followUp.reporter', '汇报人', '跟进', '测试销售'),
    variable('followUp.content', '跟进内容', '跟进', '客户希望周末到店体验'),
    variable('event.time', '汇报时间', '事件', '2026年08月05日 09:00'),
  ],
};

export function isWechatNotificationType(type: NotificationType): type is WechatNotificationType {
  return (WECHAT_NOTIFICATION_TYPES as readonly NotificationType[]).includes(type);
}
